import { Button, Upload, UploadFile } from "antd";
import { InboxOutlined, DeleteOutlined, EyeOutlined } from "@ant-design/icons";
import { memo, useCallback, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import styled from "styled-components";
import { openNotificationWithIcon } from "src/components/utils/OpenNotificationWithIcon";

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_FILES = 5;
const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/webp",
];
const ALLOWED_VIDEO_TYPES = [
  "video/mp4",
  "video/mov",
  "video/avi",
  "video/quicktime",
];

const UploadContainer = styled.div`
  width: 100%;
  margin-bottom: 1rem;
`;

const StyledUpload = styled(Upload.Dragger)`
  .ant-upload-drag {
    border: 2px dashed #d9d9d9;
    border-radius: 10px !important;
    background: #fafafa;
    transition: all 0.3s ease;

    &:hover {
      border-color: var(--blue-300);
      background: #f0f8ff;
    }
  }

  .ant-upload-drag-hover {
    border-color: var(--blue-300) !important;
    background: #f0f8ff !important;
  }
`;

const FileList = styled.div`
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FileItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem;
  border: 1px solid #e2e2e2;
  border-radius: 8px;
  background: #fafafa;
`;

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
`;

const FileName = styled.span`
  font-weight: 500;
  color: #333;
`;

const FileSize = styled.span`
  font-size: 0.875rem;
  color: #999;
`;

const FileActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  color: #666;
  transition: all 0.2s ease;

  &:hover {
    background: #f0f0f0;
    color: var(--blue-300);
  }

  &.delete:hover {
    color: #ff4d4f;
  }
`;

const InstructionsContainer = styled.div`
  width: 100%;
  margin-bottom: 1rem;
`;

const StyledTextarea = styled.textarea<{ $hasError?: boolean }>`
  width: 100%;
  min-height: 120px;
  padding: 0.75rem 1rem;
  border: 1px solid ${(props) => (props.$hasError ? "red" : "#d9d9d9")};
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.5;
  resize: vertical;
  outline: none;
  transition: border-color 0.3s ease;

  &:focus {
    border-color: var(--blue-300);
    box-shadow: 0 0 0 2px rgba(37, 62, 198, 0.1);
  }

  &::placeholder {
    color: #999;
  }
`;

const ErrorText = styled.small`
  color: red;
  display: block;
  margin-top: 0.25rem;
  font-size: 12px;
`;

const CharacterCount = styled.div`
  text-align: right;
  font-size: 12px;
  color: #666;
  margin-top: 0.25rem;
`;

const StyledLabel = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #333;
  font-size: 14px;
`;

interface FileWithPreview extends File {
  id: string;
  preview?: string;
}

function SpecialAreas({ onSubmit }) {
  const {
    formState: { errors },
    register,
    setValue,
    getValues,
    watch,
    clearErrors,
    setError,
  } = useFormContext();

  const [files, setFiles] = useState<UploadFile<FileWithPreview>[]>(() => {
    return getValues("specialAreasFiles") || [];
  });
  const instructions = watch("specialAreasInstructions") || "";

  const validateFile = useCallback(
    (file: File): string | null => {
      const allowedTypes = [...ALLOWED_IMAGE_TYPES, ...ALLOWED_VIDEO_TYPES];

      if (!allowedTypes.includes(file.type)) {
        return `File type not supported. Please upload images (JPG, PNG, GIF, WebP) or videos (MP4, MOV, AVI).`;
      }

      if (files.findIndex((f) => f.name === file.name) !== -1) {
        return `File "${file.name}" already exists.`;
      }

      if (file.size > MAX_FILE_SIZE) {
        return `File size too large. Maximum size is 5MB.`;
      }

      return null;
    },
    [files]
  );

  const createFilePreview = (file: File): string | undefined => {
    if (ALLOWED_IMAGE_TYPES.includes(file.type)) {
      return URL.createObjectURL(file);
    }
    return undefined;
  };

  const handleFileUpload = useCallback(
    (info: any) => {
      const { file, fileList } = info;
      const newFile = file;

      const error = validateFile(newFile);
      if (error) {
        openNotificationWithIcon("error", "Upload Error", error);
        return;
      }

      if (files.length + fileList.length > MAX_FILES) {
        setError("specialAreasFiles", {
          message: `Maximum ${MAX_FILES} files allowed.`,
        });
        return;
      }

      if (errors.specialAreasFiles) {
        clearErrors("specialAreasFiles");
      }

      const fileWithPreview = newFile;
      fileWithPreview.preview = createFilePreview(newFile);

      setFiles((prev) => [...prev, fileWithPreview]);
    },
    [
      validateFile,
      files.length,
      errors.specialAreasFiles,
      setError,
      clearErrors,
    ]
  );

  useEffect(() => {
    setValue("specialAreasFiles", files);
  }, [files, setValue]);

  const removeFile = useCallback((fileId: string) => {
    setFiles((prev) => {
      const fileToRemove = prev.find((f) => f.uid === fileId);
      if (fileToRemove?.preview) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      return prev.filter((f) => f.uid !== fileId);
    });
  }, []);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
        alignItems: "flex-start",
        justifyContent: "center",
        marginBottom: "2rem",
      }}
    >
      <p>
        Please upload photos or videos of any specific areas, items, or surfaces
        that require special attention during your cleaning service. This helps
        our team understand your exact needs and provide the best possible
        service.
      </p>

      <UploadContainer>
        <StyledLabel htmlFor="files">
          Upload images or videos of special areas
        </StyledLabel>
        <StyledUpload
          id="files"
          name="files"
          multiple
          beforeUpload={() => false} // Prevent auto upload
          onChange={handleFileUpload}
          fileList={files}
          showUploadList={false}
          accept={[...ALLOWED_IMAGE_TYPES, ...ALLOWED_VIDEO_TYPES].join(",")}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined
              style={{ fontSize: "48px", color: "var(--blue-300)" }}
            />
          </p>
          <p
            className="ant-upload-text"
            style={{ fontSize: "14px", fontWeight: "500" }}
          >
            Click or drag files to this area to upload
          </p>
          <p
            className="ant-upload-hint"
            style={{ color: "#666", fontSize: "12px" }}
          >
            Support for images (JPG, PNG, GIF, WebP) and videos (MP4, MOV, AVI).
            Max 5MB per file, up to 5 files total.
          </p>
        </StyledUpload>

        {files.length > 0 && (
          <FileList>
            {files.map((file) => (
              <FileItem key={file.name}>
                <FileInfo>
                  {file.preview && (
                    <img
                      src={file.preview}
                      alt={file.name}
                      style={{
                        width: "40px",
                        height: "40px",
                        objectFit: "cover",
                        borderRadius: "4px",
                      }}
                    />
                  )}
                  <div>
                    <FileName>{file.name}</FileName>
                    <FileSize>
                      &nbsp;({formatFileSize(file.size || 0)})
                    </FileSize>
                  </div>
                </FileInfo>
                <FileActions>
                  {file.preview && (
                    <ActionButton
                      type="button"
                      onClick={() => window.open(file.preview, "_blank")}
                      title="Preview"
                    >
                      <EyeOutlined />
                    </ActionButton>
                  )}
                  <ActionButton
                    type="button"
                    onClick={() => removeFile(file.uid)}
                    title="Remove"
                    className="delete"
                  >
                    <DeleteOutlined />
                  </ActionButton>
                </FileActions>
              </FileItem>
            ))}
          </FileList>
        )}

        {errors.specialAreasFiles && (
          <ErrorText>{errors.specialAreasFiles.message}</ErrorText>
        )}
      </UploadContainer>

      <InstructionsContainer>
        <StyledLabel htmlFor="specialAreasInstructions">
          Additional Instructions
        </StyledLabel>
        <StyledTextarea
          id="specialAreasInstructions"
          {...register("specialAreasInstructions", {
            maxLength: {
              value: 1000,
              message: "Instructions cannot exceed 1000 characters",
            },
          })}
          placeholder="Please provide any specific instructions for cleaning these areas..."
          $hasError={!!errors.specialAreasInstructions}
        />
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 5,
            width: "100%",
          }}
        >
          {errors.specialAreasInstructions && (
            <ErrorText>{errors.specialAreasInstructions.message}</ErrorText>
          )}
          <CharacterCount>{instructions.length}/1000 characters</CharacterCount>
        </div>
      </InstructionsContainer>

      <Button
        htmlType="submit"
        type="primary"
        className="next-button w-full"
        onClick={onSubmit}
      >
        Next
      </Button>
    </div>
  );
}

export default memo(SpecialAreas);
